import { SUPABASE_URL, getDbHeaders, cutoffDateISO } from "./client";

// Map camelCase app fields <-> snake_case DB columns
export function toRow(entry, date) {
  const row = {
    date:       date,
    section:    entry.section    || "",
    status:     entry.status     || "",
    airline:    entry.airline    || "",
    in_airline: entry.inAirline  || "",
    in_flt:     entry.inFlt      || "",
    out_airline:entry.outAirline || "",
    out_flt:    entry.outFlt     || "",
    origin:     entry.origin     || "",
    dest:       entry.dest       || "",
    sta:        entry.sta        || "",
    eta:        entry.eta        || "",
    ata:        entry.ata        || "",
    std:        entry.std        || "",
    etd:        entry.etd        || "",
    atd:        entry.atd        || "",
    ttl_pax:    entry.ttlPax     || "",
    pax_exp:    entry.paxExp     || "",
    pax_rbk:    entry.paxRbk     || "",
    ttl_bags:   entry.ttlBags    || "",
    bags_exp:   entry.bagsExp    || "",
    remark:     entry.remark     || "",
    cancelled:  entry.cancelled  || false,
    in_gate:         entry.inGate        || "",
    out_gate:        entry.outGate       || "",
    in_gate_manual:  entry.inGateManual  || false,
    out_gate_manual: entry.outGateManual || false,
    will_hold:  entry.willHold  || false,
    hold_until: entry.holdUntil || "",
    pinned:     entry.pinned     || false,
    ...(entry.autoImported ? { auto_imported: true } : {}),
    ...(entry.scco_id   ? { scco_id: entry.scco_id }     : {}),
    ...(entry.scco_name ? { scco_name: entry.scco_name } : {}),
  };
  if (entry.id !== undefined) row.id = entry.id;
  return row;
}

export function fromRow(row) {
  return {
    id:         row.id,
    section:    row.section,
    status:     row.status,
    airline:    row.airline    ? row.airline.toUpperCase()    : row.airline,
    inAirline:  row.in_airline ? row.in_airline.toUpperCase() : row.in_airline,
    inFlt:      row.in_flt     ? row.in_flt.toUpperCase()     : row.in_flt,
    outAirline: row.out_airline? row.out_airline.toUpperCase(): row.out_airline,
    outFlt:     row.out_flt    ? row.out_flt.toUpperCase()    : row.out_flt,
    origin:     row.origin     ? row.origin.toUpperCase()     : row.origin,
    dest:       row.dest       ? row.dest.toUpperCase()       : row.dest,
    sta:        row.sta,
    eta:        row.eta,
    ata:        row.ata,
    std:        row.std,
    etd:        row.etd,
    atd:        row.atd,
    ttlPax:     row.ttl_pax,
    paxExp:     row.pax_exp,
    paxRbk:     row.pax_rbk,
    ttlBags:    row.ttl_bags,
    bagsExp:    row.bags_exp,
    remark:     row.remark,
    cancelled:  row.cancelled,
    inGate:        row.in_gate         || "",
    outGate:       row.out_gate        || "",
    inGateManual:  row.in_gate_manual  || false,
    outGateManual: row.out_gate_manual || false,
    autoImported: row.auto_imported || false,
    willHold:   row.will_hold  || false,
    holdUntil:  row.hold_until || "",
    scco_id:    row.scco_id   || null,
    scco_name:  row.scco_name || null,
    pinned:     row.pinned    || false,
  };
}

export async function dbPinEntry(id, pinned) {
  const res = await fetch(SUPABASE_URL + "/rest/v1/connections?id=eq." + id, {
    method: "PATCH",
    headers: await getDbHeaders(),
    body: JSON.stringify({ pinned }),
  });
  if (!res.ok) console.error("Pin error", await res.text());
}

export async function dbLoadEntries(date) {
  const res = await fetch(
    SUPABASE_URL + "/rest/v1/connections?date=eq." + date + "&order=id.desc",
    { headers: await getDbHeaders() }
  );
  if (!res.ok) { console.error("Load error", await res.text()); return []; }
  return (await res.json()).map(fromRow);
}

export async function dbPurgeOldEntries() {
  // Delete entries older than 7 days
  await fetch(SUPABASE_URL + "/rest/v1/connections?date=lt." + cutoffDateISO(7), {
    method: "DELETE",
    headers: await getDbHeaders(),
  });
}

export async function dbPurgeOldAnalytics() {
  // Delete analytics rows older than 90 days
  await fetch(SUPABASE_URL + "/rest/v1/connection_analytics?date=lt." + cutoffDateISO(90), {
    method: "DELETE",
    headers: await getDbHeaders(),
  });
}

function toAnalyticsRow(entry, date) {
  return {
    id:        entry.id,
    date:      date,
    airline:   entry.airline    || "",
    in_airline:entry.inAirline  || "",
    in_flt:    entry.inFlt      || "",
    eta:       entry.eta        || "",
    sta:       entry.sta        || "",
    origin:    entry.origin     || "",
    dest:      entry.dest       || "",
    ttl_pax:   entry.ttlPax     || "",
    bags_exp:  entry.bagsExp    || "",
    ttl_bags:  entry.ttlBags    || "",
    status:    entry.status     || "",
    scco_name: entry.scco_name  || null,
    will_hold: entry.willHold   || false,
  };
}

async function upsertAnalytics(row) {
  const res = await fetch(SUPABASE_URL + "/rest/v1/connection_analytics", {
    method: "POST",
    headers: await getDbHeaders({ "Prefer": "resolution=merge-duplicates,return=minimal" }),
    body: JSON.stringify(row),
  });
  if (!res.ok) console.error("Analytics upsert error", await res.text());
}

export async function dbInsertEntry(entry, date) {
  const res = await fetch(SUPABASE_URL + "/rest/v1/connections", {
    method: "POST",
    headers: await getDbHeaders({ "Prefer": "return=representation" }),
    body: JSON.stringify(toRow(entry, date)),
  });
  if (!res.ok) { console.error("Insert error", await res.text()); return; }
  const [inserted] = await res.json();
  if (inserted?.id) {
    await upsertAnalytics(toAnalyticsRow({ ...entry, id: inserted.id }, date));
  }
}

export async function dbUpdateEntry(entry, date) {
  const res = await fetch(SUPABASE_URL + "/rest/v1/connections?id=eq." + entry.id, {
    method: "PATCH",
    headers: await getDbHeaders(),
    body: JSON.stringify(toRow(entry, date)),
  });
  if (!res.ok) { console.error("Update error", await res.text()); return; }
  await upsertAnalytics(toAnalyticsRow(entry, date));
}

export async function dbDeleteEntry(id) {
  const res = await fetch(SUPABASE_URL + "/rest/v1/connections?id=eq." + id, {
    method: "DELETE",
    headers: await getDbHeaders(),
  });
  if (!res.ok) console.error("Delete error", await res.text());
}

export async function dbBatchUpdateEntries(updates) {
  await Promise.all(updates.map(async ({ id, dbPatch }) =>
    fetch(SUPABASE_URL + "/rest/v1/connections?id=eq." + id, {
      method: "PATCH",
      headers: await getDbHeaders(),
      body: JSON.stringify(dbPatch),
    })
  ));
}
